"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DressType } from "@/types/gallery";
import { getPublicImageUrl } from "@/utils/imageUtils";
import {
  galleryCustomizeHref,
  galleryDressDetailHref,
} from "@/utils/galleryNavigation";
import { formatPublicDressPrice } from "@/constants/pricing";

export interface GalleryDressItem {
  id: string;
  prompt: string | null;
  image_url: string | null;
  original_image_path?: string | null;
  back_image_url?: string | null;
  back_image_path?: string | null;
  color?: string | null;
  back_prompt?: string | null;
  preset_type?: string | null;
  dress_length?: string | null;
  sleeve_style?: string | null;
  neckline_style?: string | null;
  waistline_style?: string | null;
  silhouette_fit?: string | null;
  fabric_texture?: string | null;
  hasQuotes?: boolean;
  quoteCount?: number;
  minPrice?: number | null;
  currency?: string | null;
}

interface FilteredGallerySectionProps {
  title?: string;
  subtitle?: string;
  dressType: DressType;
  initialData?: GalleryDressItem[];
  browseAllText?: string;
  browseAllHref?: string;
  className?: string;
}

const ITEMS_TO_SHOW = 8;

export default function FilteredGallerySection({
  title = "Popular Wedding Dress Styles",
  subtitle,
  dressType,
  initialData = [],
  browseAllText = "Browse all",
  browseAllHref,
  className = "",
}: FilteredGallerySectionProps) {
  const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);
  const router = useRouter();
  const pathname = usePathname() ?? "";

  const resolvedBrowseAllHref =
    browseAllHref ??
    (dressType !== "all" ? `/gallery/${dressType}` : "/gallery");

  const galleryDresses = initialData.slice(0, ITEMS_TO_SHOW);
  const hasData = galleryDresses.length > 0;
  const displayItems = hasData
    ? galleryDresses
    : Array.from({ length: ITEMS_TO_SHOW });

  const handleCustomize = useCallback(
    (dress: GalleryDressItem) => {
      router.push(galleryCustomizeHref(dress, "home_gallery"));
    },
    [router]
  );

  const handleCardAction = useCallback(
    (dress: GalleryDressItem, e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      if (dress.hasQuotes) {
        router.push(galleryDressDetailHref(dress.id, pathname));
      } else {
        handleCustomize(dress);
      }
    },
    [router, pathname, handleCustomize]
  );

  return (
    <section className={`bg-[var(--background)] py-16 sm:py-24 ${className}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 sm:mb-6">
            {title}
          </h2>
          {subtitle && (
            <p className="text-lg sm:text-xl text-foreground opacity-80 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        {dressType !== "all" && (
          <div className="flex justify-end mb-4">
            <Link
              href={resolvedBrowseAllHref}
              className="text-sm font-medium text-primary hover:text-primary hover:underline"
            >
              {browseAllText}
            </Link>
          </div>
        )}

        <div className="relative">
          <div className="flex gap-4 overflow-x-auto pb-4 pt-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin">
            {displayItems.map((dress, idx) => {
              const isHovered = hoveredCardIndex === idx;
              const isPlaceholder = !hasData || !dress;
              const dressData = isPlaceholder
                ? null
                : (dress as GalleryDressItem);

              return (
                <div
                  key={isPlaceholder ? `skeleton-${idx}` : dressData!.id}
                  className="shrink-0 w-56 sm:w-52 md:w-56 p-2"
                  style={{
                    transform: isHovered ? "scale(1.05)" : "scale(1)",
                    transition: "transform 300ms ease-out",
                    zIndex: isHovered ? 10 : 1,
                  }}
                >
                  <Card
                    variant="interactive"
                    padding="none"
                    overflowHidden
                    className={`relative aspect-[3/4] rounded-xl cursor-pointer ${
                      isHovered ? "border-primary/40" : ""
                    }`}
                    onClick={e =>
                      !isPlaceholder &&
                      dressData &&
                      handleCardAction(dressData, e)
                    }
                    onMouseEnter={() => setHoveredCardIndex(idx)}
                    onMouseLeave={() => setHoveredCardIndex(null)}
                    role="button"
                    tabIndex={0}
                    aria-label={
                      isPlaceholder
                        ? "Loading design"
                        : dressData?.hasQuotes
                          ? `Buy dress: ${dressData?.prompt || "Dress design"}`
                          : `Customize: ${dressData?.prompt || "Dress design"}`
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (!isPlaceholder && dressData) {
                          handleCardAction(dressData);
                        }
                      }
                    }}
                  >
                    {isPlaceholder ? (
                      <div className="absolute inset-0 animate-pulse bg-muted" />
                    ) : dressData?.image_url ? (
                      <Image
                        src={dressData.image_url}
                        alt={dressData.prompt || "Dress design"}
                        fill
                        className="object-cover"
                        sizes="208px"
                        loading="lazy"
                        fetchPriority="low"
                      />
                    ) : dressData?.original_image_path ? (
                      <Image
                        src={getPublicImageUrl(dressData.original_image_path)}
                        alt={dressData.prompt || "Dress design"}
                        fill
                        className="object-cover"
                        sizes="208px"
                        loading="lazy"
                        fetchPriority="low"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                        No image
                      </div>
                    )}
                    {!isPlaceholder &&
                      dressData?.hasQuotes &&
                      dressData.minPrice != null && (
                        <Badge
                          className="absolute top-2 right-2 !rounded-lg bg-success text-success-foreground border-0 shadow-md text-center flex flex-col py-1 px-2 text-[10px]"
                          size="md"
                        >
                          <span>
                            {formatPublicDressPrice(
                              dressData.minPrice,
                              dressData.currency || "USD"
                            )}
                          </span>
                        </Badge>
                      )}
                    {!isPlaceholder && (
                      <div
                        className={`absolute inset-0 transition-colors duration-300 pointer-events-none ${
                          isHovered ? "bg-black/5" : "bg-black/0"
                        }`}
                      />
                    )}
                  </Card>
                  {!isPlaceholder && (
                    <div className="mt-2 space-y-1">
                      {dressData?.prompt && (
                        <p className="text-xs text-foreground opacity-70 line-clamp-1">
                          {dressData.prompt}
                        </p>
                      )}
                      <Button
                        variant={dressData?.hasQuotes ? "success" : "outline"}
                        size="sm"
                        className="min-h-11 w-full text-xs"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (dressData) handleCardAction(dressData, e);
                        }}
                      >
                        {dressData?.hasQuotes ? "Buy Now" : "Customize"}
                      </Button>
                      {dressData?.hasQuotes ? (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="min-h-11 w-full text-xs"
                        >
                          <Link
                            href={galleryCustomizeHref(
                              dressData,
                              "home_gallery"
                            )}
                            onClick={event => event.stopPropagation()}
                          >
                            Customize
                          </Link>
                        </Button>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
